import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {Button} from "primereact/button";
import {FloatLabel} from "primereact/floatlabel";
import {variables} from "../static/contracts_variables";
import {InputText} from "primereact/inputtext";
import {bootstrapColors, primeIcons} from "../static/colors_icons_ui";
import IconSelector from "./widgets/IconSelector";
import ColorSelector from "./widgets/ColorSelector";
import VariableSelector from "./widgets/VariableSelector";
import CreatingCard from "./widgets/CreatingCard";
import {base, get_contract_types} from "../../api";
import DisabledFormFieldsForEditForm from "./widgets/DisabledFormFieldsForEditForm";
import {FileUpload} from "primereact/fileupload";
import {ProgressBar} from "primereact/progressbar";
import {Tooltip} from "primereact/tooltip";
import {FaFile} from "react-icons/fa";
import {t} from "i18next";

export default function ProcessesDocumentationEditForm({
	process,
	setProcess,
	fetchProcesses,
	editDialogVisible,
	setEditDialogVisible,
	onSave,
	toast,
}) {
	const [selectedKeys, setSelectedKeys] = useState([]);
	const availableKeys = variables.filter(
		item =>
			item.type &&
			item.type !== "autogenerated" &&
			!selectedKeys.find(key => key.field_name === item.field_name),
	);
	const [inputValues, setInputValues] = useState({});
	const [formName, setFormName] = useState("");
	const [slug, setSlug] = useState("");
	const [selectedColor, setSelectedColor] = useState(bootstrapColors[0].color);
	const [selectedIcon, setSelectedIcon] = useState(primeIcons[0]);
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [isFormValid, setIsFormValid] = useState(true);

	const validateForm = () => {
		const allInputsFilled = Object.values(inputValues).every(value => value !== "");
		const atLeastOneCheckboxChecked = selectedTypes.length > 0;
		setIsFormValid(allInputsFilled && atLeastOneCheckboxChecked);
	};

	useEffect(() => {
		if (process) {
			setFormName(process.name || "");
			setSlug(process.slug || "");
			setSelectedColor(process.color || bootstrapColors[0].color);
			setSelectedIcon(process.icon || primeIcons[0]);
			setSelectedTypes(process.types || []);
			setInputValues(process.values || {});
			setSelectedKeys(JSON.parse(process.form_fields) || []);
		}
	}, [process]);

	const handleCancel = async () => {
		setEditDialogVisible(false);
		toast.current.show({
			severity: "error",
			summary: "Dialog closed",
			detail: "Process edited form closed",
			life: 3000,
		});
	};

	const handleSave = async () => {
		const updatedProcess = {
			name: formName,
			slug: slug,
			color: selectedColor,
			icon: selectedIcon,
			types: selectedTypes,
			values: inputValues,
		};
		const formData = new FormData();
		formData.append("name", formName);
		formData.append("slug", slug);
		formData.append("color", selectedColor);
		formData.append("icon", selectedIcon);
		formData.append("form_fields", JSON.stringify(selectedKeys));
		Object.keys(inputValues).forEach(key => {
			formData.append(`inputValues[${key}]`, inputValues[key]);
		});

		const files = fileUploadRef.current?.getFiles() || [];
		files.forEach(file => formData.append("uploaded_files", file));

		try {
			await axios.patch(`${base + get_contract_types}/${process.id}/`, formData, {
				headers: {"Content-Type": "multipart/form-data"},
			});
			fetchProcesses();
			onSave(updatedProcess);
			setEditDialogVisible(false);
			toast.current.show({
				severity: "success",
				summary: "Edited",
				detail: "Process edited successfully",
				life: 3000,
			});
		} catch (error) {
			console.error("Error saving process:", error);
			toast.current.show({
				severity: "error",
				summary: "Error",
				detail: "Failed to edit process",
				life: 3000,
			});
		}
	};
	const handleInputChange = (property, value) => {
		setInputValues(prev => ({...prev, [property]: value}));
		validateForm();
	};

	const handleRemoveInput = key => {
		setSelectedKeys(selectedKeys.filter(item => item.field_name !== key.field_name));
		setInputValues(prev => {
			const newValues = {...prev};
			delete newValues[key.field_name];
			return newValues;
		});
	};

	const handleCheckboxChange = selectedType => {
		setSelectedTypes(prev => {
			const newSelectedTypes = [...prev];
			if (newSelectedTypes.includes(selectedType)) {
				newSelectedTypes.splice(newSelectedTypes.indexOf(selectedType), 1);
			} else {
				newSelectedTypes.push(selectedType);
			}
			return newSelectedTypes;
		});
		validateForm();
	};

	const [totalSize, setTotalSize] = useState(0);
	const fileUploadRef = useRef(null);

	const onTemplateSelect = e => {
		let _totalSize = totalSize;
		let files = e.files;

		Object.keys(files).forEach(key => {
			_totalSize += files[key].size || 0;
		});

		setTotalSize(_totalSize);
	};

	const onTemplateUpload = e => {
		let _totalSize = 0;

		e.files.forEach(file => {
			_totalSize += file.size || 0;
		});

		setTotalSize(_totalSize);
		toast.current.show({severity: "info", summary: "Success", detail: "File Uploaded"});
	};

	const onTemplateRemove = (file, callback) => {
		setTotalSize(totalSize - file.size);
		callback();
	};

	const onTemplateClear = () => {
		setTotalSize(0);
	};

	const headerTemplate = options => {
		const {className, chooseButton} = options;
		const value = totalSize / 10000;
		const formatedValue =
			fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : "0 B";

		return (
			<div
				className={className}
				style={{backgroundColor: "transparent", display: "flex", alignItems: "center"}}>
				{chooseButton}
				<div className="flex align-items-center gap-3 ml-auto">
					<span>{formatedValue} / 10 MB</span>
					<ProgressBar
						value={value}
						showValue={false}
						style={{width: "10rem", height: "12px"}}></ProgressBar>
				</div>
			</div>
		);
	};

	const itemTemplate = (file, props) => {
		return (
			<div className="w-100 d-flex justify-content-between align-items-center py-0">
				<span className="text-start text-primary">
					{file.name}
					<small className="text-info ms-1">({props.formatSize})</small>
				</span>
				<Button
					type="button"
					icon="pi pi-times"
					text
					rounded
					severity="danger"
					onClick={() => onTemplateRemove(file, props.onRemove)}
				/>
			</div>
		);
	};

	const emptyTemplate = () => {
		return (
			<div className="flex align-items-center flex-column">
				<i
					className="pi pi-image mt-2 p-3"
					style={{
						fontSize: "1.5em",
						borderRadius: "50%",
						backgroundColor: "var(--surface-b)",
						color: "var(--surface-d)",
					}}></i>
				<span style={{fontSize: "1.2em", color: "var(--text-color-secondary)"}} className="mt-2">
					Drag and Drop Image Here
				</span>
			</div>
		);
	};

	const chooseOptions = {
		icon: "pi pi-arrow-up",
		iconOnly: true,
		className: "custom-choose-btn p-button-rounded p-button-outlined",
	};
	useEffect(() => {
		const dialogContent = document.querySelector(".p-dialog .p-dialog-content");
		if (dialogContent) {
			dialogContent.scrollTop = 0;
		}
	}, [editDialogVisible]);
	return (
		<div className="col-12">
			<div className="row">
				<div className="col-xl-3 col-lg-4 col-md-4 col-12 stretch-card p-3">
					<CreatingCard
						selectedIcon={selectedIcon}
						formName={formName}
						selectedColor={selectedColor}
					/>
				</div>
				<div className="col-xl-9 col-lg-8 col-md-8 col-12 stretch-card p-3">
					<div className="row">
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="formName"
								value={formName}
								onChange={e => {
									setFormName(e.target.value);
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									);
								}}
							/>
							<label htmlFor="formName">Card Title</label>
						</FloatLabel>
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								id="slug"
								value={slug}
								onChange={e =>
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									)
								}
							/>
							<label htmlFor="slug">Slug/url</label>
						</FloatLabel>

						<IconSelector
							selectedIcon={selectedIcon}
							setSelectedIcon={setSelectedIcon}
							selectedColor={selectedColor}
						/>
						<ColorSelector selectedColor={selectedColor} setSelectedColor={setSelectedColor} />
					</div>
				</div>

				<VariableSelector
					availableKeys={availableKeys}
					handleChipClick={key => setSelectedKeys([...selectedKeys, key])}
				/>
				{selectedKeys.length > 0 ? (
					selectedKeys.map(item => (
						<DisabledFormFieldsForEditForm
							item={{
								...item,
								variable: item.field_name,
								variable_description: item.description,
							}}
							remove={true}
							disabled={true}
							inputValues={inputValues}
							handleInputChange={handleInputChange}
							handleRemoveInput={handleRemoveInput}
						/>
					))
				) : (
					<div className="col-12">
						<div className="d-flex justify-content-center align-items-center p-5 opacity-50">
							No fields selected
						</div>
					</div>
				)}
				<div>
					<div className="col-12 pb-3">
						{process?.file_templates?.length > 0 && (
							<>
								<div className="fs-3 fw-bold opacity-75 border-bottom pb-3 mb-3">Files</div>
								<div className="row">
									{process.file_templates.map(file => {
										const fileName = file.file_name.split("/").pop();
										const prefixMatch = fileName.match(/^[^_]+_/);
										const cleanedFileName = prefixMatch
											? fileName.replace(prefixMatch[0], "")
											: fileName;
										return (
											<div className="col-4 d-flex align-items-center my-2" key={file.id}>
												<FaFile className="me-2 text-primary" />
												<a
													href={file.file}
													className="text-primary w-auto text-truncate text-capitalize">
													{cleanedFileName
														.replace(/^\d+/, "")
														.replace(/_/g, " ")
														.replace(/\.[^/.]+$/, "")
														.toLowerCase()
														.replace(/\s+/g, " ")}
												</a>
											</div>
										);
									})}
								</div>
							</>
						)}
					</div>

					<Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
					<Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />
					<Tooltip target=".custom-cancel-btn" content="Clear" position="bottom" />

					<FileUpload
						ref={fileUploadRef}
						name="demo[]"
						url="/api/upload"
						multiple
						accept=".doc, .docx, .xls, .xlsx"
						maxFileSize={1000000}
						onUpload={onTemplateUpload}
						onSelect={onTemplateSelect}
						onError={onTemplateClear}
						onClear={onTemplateClear}
						headerTemplate={headerTemplate}
						itemTemplate={itemTemplate}
						emptyTemplate={emptyTemplate}
						chooseOptions={chooseOptions}
					/>
				</div>
			</div>

			<div className="col-12 mt-5 text-center border-top pt-3">
				<Button
					label="Cancel"
					icon="pi pi-times"
					onClick={handleCancel}
					className="mx-3"
					rounded
					text
					severity="secondary"
				/>
				<Button
					label="Save"
					icon="pi pi-check"
					className="mx-3"
					rounded
					onClick={handleSave}
					autoFocus
					disabled={!isFormValid}
				/>
			</div>
		</div>
	);
}
